import React from 'react';

/**
 * Ilustraciones que se dibujan al entrar en pantalla (contenedor con `data-dibuja`,
 * ver useDibujaAlVer e index.css). Todas son decorativas: aria-hidden.
 */

const d = (ms) => ({ '--d': `${ms}ms` });

/* Plano de zonas: puerta de Urbanismo y banda de cómo funciona. */
const ZONAS = [
  [10, 10, 70, 50, 'f-residencial'], [86, 10, 50, 50, 'f-dotacio'], [142, 10, 98, 30, 'f-sistema'],
  [142, 46, 98, 14, 'f-residencial'], [10, 66, 44, 74, 'f-activitat'], [60, 66, 100, 44, 'f-residencial'],
  [60, 116, 100, 24, 'f-sistema'], [166, 66, 74, 74, 'f-urbanitzable'],
];
export const IlusZonas = () => (
  <svg viewBox="0 0 250 150" className="h-auto w-full" aria-hidden="true">
    {ZONAS.map(([x, y, w, h, f], i) => (
      <g key={i}>
        <rect className="relleno" style={{ ...d(200 + i * 110), '--o': 0.55 }} x={x} y={y} width={w} height={h} fill={`var(--${f})`} />
        <rect className="trazo" style={d(i * 70)} pathLength="1000" x={x} y={y} width={w} height={h} fill="none" stroke="oklch(var(--foreground) / 0.3)" />
      </g>
    ))}
    <rect className="trazo" style={d(1200)} pathLength="1000" x="100" y="66" width="34" height="44" fill="none" stroke="oklch(var(--ember))" strokeWidth="2.5" />
  </svg>
);

/* Puerta Territorio y sección: capas en axonometría que se separan. */
const ROMBO = '120,0 240,60 120,120 0,60';
export const IlusCapas = ({ grande = false }) => {
  const sep = grande ? 46 : 30;
  const capas = [
    { color: 'oklch(var(--foreground) / 0.35)', contenido: 'parcelas' },
    { color: 'oklch(var(--solar))', contenido: 'relieve' },
    { color: 'var(--f-dotacio)', contenido: 'agua' },
    { color: 'oklch(var(--disponible))', contenido: 'natura' },
  ];
  const alto = 120 + sep * 3 + 20;
  return (
    <svg viewBox={`0 0 260 ${alto}`} className="h-auto w-full" aria-hidden="true" fill="none">
      {capas.map((c, i) => (
        <g key={c.contenido} transform={`translate(10 ${10 + i * sep})`}>
          <g className="capa-sube" style={{ ...d(i * 140), '--desde': `${(1.5 - i) * sep * 0.6}px`, '--hasta': '0px' }}>
            <polygon points={ROMBO} fill="oklch(var(--card))" stroke="oklch(var(--border))" />
            <polygon className="trazo" style={d(200 + i * 160)} pathLength="1000" points={ROMBO} stroke={c.color} strokeWidth="1.2" />
            {c.contenido === 'parcelas' && (
              <g stroke={c.color} strokeWidth="0.8">
                <line className="trazo" style={d(500)} pathLength="1000" x1="60" y1="30" x2="180" y2="90" />
                <line className="trazo" style={d(600)} pathLength="1000" x1="120" y1="60" x2="60" y2="90" />
                <line className="trazo" style={d(700)} pathLength="1000" x1="180" y1="30" x2="120" y2="60" />
              </g>
            )}
            {c.contenido === 'relieve' && (
              <g stroke={c.color} strokeWidth="1">
                {['M40 60 Q120 26 200 60', 'M60 60 Q120 38 180 60', 'M80 60 Q120 48 160 60'].map((p, k) => (
                  <path key={p} className="trazo" style={d(600 + k * 120)} pathLength="1000" d={p} />
                ))}
              </g>
            )}
            {c.contenido === 'agua' && (
              <path className="trazo" style={d(800)} pathLength="1000" d="M20 60 C70 40 100 80 140 64 S200 50 220 60" stroke={c.color} strokeWidth="2.5" strokeLinecap="round" />
            )}
            {c.contenido === 'natura' && (
              <polygon className="relleno" style={{ ...d(1000), '--o': 1 }} points="70,58 130,30 190,62 120,96" fill="oklch(var(--disponible) / 0.25)" stroke={c.color} />
            )}
          </g>
        </g>
      ))}
    </svg>
  );
};

/* Curvas de nivel generadas a partir de una semilla (cabecera de las páginas de
   Urbanismo). Decorativas: no representan el relieve real del municipio. */
function aleatorio(semilla) {
  let h = 2166136261;
  for (const c of String(semilla)) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

export const CurvasDeNivel = ({ semilla = 'geovis', className = '' }) => {
  const r = aleatorio(semilla);
  const cx = 230 + r() * 80;
  const cy = 150 + r() * 60;
  const fases = [r() * 6, r() * 6, r() * 6];
  const curvas = Array.from({ length: 9 }, (_, k) => {
    const radio = 26 + k * 24;
    const puntos = Array.from({ length: 48 }, (_, i) => {
      const a = (i / 48) * Math.PI * 2;
      const ruido = 1 + 0.12 * Math.sin(3 * a + fases[0]) + 0.07 * Math.sin(5 * a + fases[1] + k * 0.3) + 0.05 * Math.sin(2 * a + fases[2]);
      return [cx + Math.cos(a) * radio * 1.35 * ruido, cy + Math.sin(a) * radio * ruido];
    });
    return `M${puntos.map((p) => p.map((n) => n.toFixed(1)).join(' ')).join(' L')} Z`;
  });
  return (
    <svg viewBox="0 0 480 320" className={className} aria-hidden="true" fill="none" preserveAspectRatio="xMidYMid slice">
      {curvas.map((p, k) => (
        <path
          key={k}
          className="trazo"
          style={d(k * 110)}
          pathLength="1000"
          d={p}
          stroke={k % 4 === 0 ? 'oklch(var(--acento) / 0.55)' : 'oklch(var(--foreground) / 0.16)'}
          strokeWidth={k % 4 === 0 ? 1.3 : 0.9}
        />
      ))}
      <circle className="relleno" style={d(1100)} cx={cx} cy={cy} r="4" fill="oklch(var(--acento))" />
    </svg>
  );
};
